import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { FuseScrollbarDirective } from '@fuse/directives/scrollbar';
import { ExpertManagerEventNotificationRecord } from '@libs/shared/api';
import { EmptyExpertManagerEventNotificationComponent } from '@sprint-expert-manager/app/modules/expert-manager-event-notification/empty-expert-manager-event-notification.component';
import { ExpertConsoleExpertJoinInquiryOpenedNotificationComponent } from '@sprint-expert-manager/app/modules/expert-manager-event-notification/expert-console-expert-join-inquiry-opened-notification.component';
import { ExpertConsoleManagerJoinInquiryAcceptedNotificationComponent } from '@sprint-expert-manager/app/modules/expert-manager-event-notification/expert-console-manager-join-inquiry-accepted-notification.component';
import { ExpertConsoleManagerJoinInquiryDeclinedNotificationComponent } from '@sprint-expert-manager/app/modules/expert-manager-event-notification/expert-console-manager-join-inquiry-declined-notification.component';
import { ExpertConsoleManagerJoinInquiryOpenedNotificationComponent } from '@sprint-expert-manager/app/modules/expert-manager-event-notification/expert-console-manager-join-inquiry-opened-notification.component';
import { ExpertManagerCreatedNotificationComponent } from '@sprint-expert-manager/app/modules/expert-manager-event-notification/expert-manager-created-notification.component';
import {
  ExpertManagerEventNotificationService,
  injectExpertManagerEventNotificationService,
} from '@sprint-expert-manager/app/modules/expert-manager-event-notification/expert-manager-event-notification.service';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { NgScrollbar } from 'ngx-scrollbar';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-expert-manager-event-notification',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    NgClass,
    NgTemplateOutlet,
    MatSlideToggleModule,
    FormsModule,
    EmptyExpertManagerEventNotificationComponent,
    NgScrollbar,
    InfiniteScrollDirective,
    ExpertManagerCreatedNotificationComponent,
    ExpertConsoleExpertJoinInquiryOpenedNotificationComponent,
    ExpertConsoleManagerJoinInquiryOpenedNotificationComponent,
    ExpertConsoleManagerJoinInquiryAcceptedNotificationComponent,
    ExpertConsoleManagerJoinInquiryDeclinedNotificationComponent,
  ],
  providers: [ExpertManagerEventNotificationService],
  template: `
    <!-- ExpertManagerEventNotifications toggle -->
    <button
      mat-icon-button
      (click)="openPanel()"
      #expertManagerEventNotificationsOrigin>
      @if (vm.unreadCount$$() > 0) {
        <span
          class="z-999 absolute left-0 right-0 top-0 flex h-3 items-center justify-center">
          <span
            class="bg-primary-500 text-primary-50 ml-4 mt-2.5 flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full px-1 text-xs font-medium">
            {{ vm.unreadCount$$() }}
          </span>
        </span>
      }
      <mat-icon [svgIcon]="'heroicons_outline:inbox'" />
    </button>

    <!-- ExpertManagerEventNotifications panel -->
    <ng-template #expertManagerEventNotificationsPanel>
      <div
        class="sm:min-w-90 sm:w-90 fixed inset-0 flex flex-col overflow-hidden shadow-lg sm:static sm:inset-auto sm:rounded-2xl">
        <!-- Header -->
        <div
          class="bg-accent text-on-accent flex shrink-0 items-center py-4 pl-6 pr-4">
          <div class="-ml-1 mr-3 sm:hidden">
            <button mat-icon-button (click)="closePanel()">
              <mat-icon
                class="icon-size-5 text-current"
                [svgIcon]="'heroicons_solid:x'" />
            </button>
          </div>
          <div class="text-lg font-medium leading-10">이벤트 알림</div>
          <div class="ml-auto">
            <button
              mat-icon-button
              [matTooltip]="'모두 읽음 표시'"
              [disabled]="vm.unreadCount$$() === 0"
              (click)="vm.readAllExpertManagerEventNotifications()">
              <mat-icon
                class="icon-size-5 text-current"
                [svgIcon]="'heroicons_solid:envelope-open'" />
            </button>
          </div>
        </div>

        <!-- Content -->
        <div
          class="bg-card sm:max-h-120 relative flex flex-auto flex-col divide-y">
          <ng-scrollbar orientation="vertical">
            <div
              infiniteScroll
              [scrollWindow]="false"
              [infiniteScrollDistance]="4"
              (scrolled)="vm.fetchNextExpertManagerEventNotificationSlice()">
              <div class="flex flex-row justify-end px-4 py-2">
                <mat-slide-toggle
                  labelPosition="before"
                  color="primary"
                  [checked]="vm.showOnlyUnread$$()"
                  (change)="
                    vm.toggleShowOnlyUnreadExpertManagerEventNotifications(
                      $event.checked
                    )
                  ">
                  안 읽은 알림만 보기
                </mat-slide-toggle>
              </div>

              <!-- ExpertManagerEventNotifications -->
              @for (item of vm.slice$$()?.content; track item.id) {
                <div
                  class="group flex hover:bg-gray-50 dark:hover:bg-black dark:hover:bg-opacity-5"
                  [ngClass]="{ unread: !item.readAt }">
                  <!-- ExpertManagerEventNotification without a link -->
                  <div class="flex flex-auto py-5 pl-6">
                    <ng-container
                      *ngTemplateOutlet="
                        expertManagerEventNotificationContent
                      " />
                  </div>
                  <!-- Actions -->
                  <div class="relative my-5 ml-2 mr-6 flex flex-col">
                    <!-- Indicator -->
                    <button
                      class="h-6 min-h-6 w-6"
                      mat-icon-button
                      (click)="vm.toggleExpertManagerEventNotification(item)"
                      [matTooltip]="item.readAt ? '안 읽음 표시' : '읽음 표시'">
                      <span
                        class="h-2 w-2 rounded-full"
                        [ngClass]="{
                          'bg-gray-400 sm:opacity-0 sm:group-hover:opacity-100 dark:bg-gray-500':
                            item.readAt,
                          'bg-primary': !item.readAt,
                        }"></span>
                    </button>
                  </div>
                </div>
                <!-- ExpertManagerEventNotification content template -->
                <ng-template #expertManagerEventNotificationContent>
                  @switch (item.type) {
                    @case ('ExpertManagerCreated') {
                      <app-expert-manager-created-notification
                        class="w-full"
                        [expertManagerEventNotification]="item"
                        (click)="moveToAccountSettings(item)" />
                    }
                    @case ('ExpertConsoleExpertJoinInquiryOpened') {
                      <app-expert-console-expert-join-inquiry-opened-notification
                        class="w-full"
                        [expertManagerEventNotification]="item"
                        (click)="moveToExpertConsoleParticipants(item)" />
                    }
                    @case ('ExpertConsoleManagerJoinInquiryOpened') {
                      <app-expert-console-manager-join-inquiry-opened-notification
                        class="w-full"
                        [expertManagerEventNotification]="item"
                        (click)="moveToExpertConsoleParticipants(item)" />
                    }
                    @case ('ExpertConsoleManagerJoinInquiryAccepted') {
                      <app-expert-console-manager-join-inquiry-accepted-notification
                        class="w-full"
                        [eventNotification]="item"
                        (click)="moveToExpertConsoleParticipants(item)" />
                    }
                    @case ('ExpertConsoleManagerJoinInquiryDeclined') {
                      <app-expert-console-manager-join-inquiry-declined-notification
                        class="w-full"
                        [eventNotification]="item"
                        (click)="readEventNotification(item)" />
                    }
                  }
                </ng-template>
              }
              <!-- No expertManagerEventNotifications -->
              @if (!vm.slice$$().content || !vm.slice$$().content.length) {
                <app-empty-expert-manager-event-notification class="p-16" />
              }
            </div>
          </ng-scrollbar>
        </div>
      </div>
    </ng-template>
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpertManagerEventNotificationComponent
  implements OnInit, OnDestroy
{
  readonly vm = injectExpertManagerEventNotificationService();
  readonly router = inject(Router);

  @ViewChild('expertManagerEventNotificationsOrigin')
  private _expertManagerEventNotificationsOrigin: MatButton;
  @ViewChild('expertManagerEventNotificationsPanel')
  private _expertManagerEventNotificationsPanel: TemplateRef<any>;
  @ViewChildren(FuseScrollbarDirective)
  private _fuseScrollbarDirectives: QueryList<FuseScrollbarDirective>;

  private _overlayRef: OverlayRef;

  /**
   * Constructor
   */
  constructor(
    private _overlay: Overlay,
    private _viewContainerRef: ViewContainerRef
  ) {
    effect(() => {
      if (this.vm.showOnlyUnread$$()) {
        if (this._fuseScrollbarDirectives) {
          this._fuseScrollbarDirectives.forEach(directive => {
            directive.scrollToTop(0);
          });
        }
      }
    });
  }

  ngOnInit(): void {
    this.vm.fetchExpertManagerEventNotificationSlice();
    this.vm.fetchExpertMangerEventNotificationUnreadCount();
  }

  ngOnDestroy(): void {
    // Dispose the overlay
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }
  }

  openPanel(): void {
    this.vm.fetchExpertManagerEventNotificationSlice();
    // Return if the expertManagerEventNotifications panel or its origin is not defined
    if (
      !this._expertManagerEventNotificationsPanel ||
      !this._expertManagerEventNotificationsOrigin
    ) {
      return;
    }

    // Create the overlay if it doesn't exist
    if (!this._overlayRef) {
      this._createOverlay();
    }

    // Attach the portal to the overlay
    this._overlayRef.attach(
      new TemplatePortal(
        this._expertManagerEventNotificationsPanel,
        this._viewContainerRef
      )
    );
  }

  closePanel(): void {
    this._overlayRef.detach();
  }

  moveToAccountSettings(
    expertManagerEventNotification: ExpertManagerEventNotificationRecord
  ) {
    this.vm.readExpertManagerEventNotification(expertManagerEventNotification);
    this.router.navigate(['/settings/account'], {
      onSameUrlNavigation: 'reload',
    });
    this.closePanel();
  }

  moveToExpertConsoleParticipants(item: ExpertManagerEventNotificationRecord) {
    this.vm.readExpertManagerEventNotification(item);
    this.router.navigate(['/expert-console-list', item.expertConsole.id], {
      onSameUrlNavigation: 'reload',
      queryParams: {
        tab: 'participants',
      },
    });
    this.closePanel();
  }

  readEventNotification(
    eventNotification: ExpertManagerEventNotificationRecord
  ): void {
    this.vm.readExpertManagerEventNotification(eventNotification);
  }

  private _createOverlay(): void {
    // Create the overlay
    this._overlayRef = this._overlay.create({
      hasBackdrop: true,
      backdropClass: 'fuse-backdrop-on-mobile',
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(
          this._expertManagerEventNotificationsOrigin._elementRef.nativeElement
        )
        .withLockedPosition(true)
        .withPush(true)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          },
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom',
          },
        ]),
    });

    this._overlayRef.backdropClick().subscribe(() => {
      this._overlayRef.detach();
    });
  }
}
